import { DatePipe, formatDate } from "@angular/common";
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { EmailPayloadBase } from "@models/email-templates/email-payload-base";
import { IEmailPreviewResponse } from "@models/email-templates/email-review-response.interface";
import { EmailTemplate } from "@models/email-templates/email-template";
import {
  EmailTemplateKey,
  EmailTemplatesHelper,
  IEmailAndTextTemplatePayloadData
} from "@models/email-templates/email-templates-helper";
import { ShadowDomViewerComponent } from "@shared/components/email-preview/shadow-dom-viewer/shadow-dom-viewer.component";
import { DocumentTypePipe } from "@shared/pipes/document-type.pipe";
import { EmailsApiService } from "@shared/services/emails-api.service";
import { TextMessageApiService } from "@shared/services/sms-templates.service";
import { Guid } from "@shared/utils/guid.helper";
import { setControlDisabled } from "@shared/utils/set-control-disabled";
import { NgxSkeletonLoaderModule } from "ngx-skeleton-loader";
import { catchError, Observable, of, tap } from "rxjs";
import { ChangeAuthorisation } from "@models/documents/change-authorisation";
import { Contact } from "@models/common/contact";
import { DocumentSigning } from "@models/documents/document-signing";
import { LodgementDeadline } from "@models/documents/lodgement-deadline";
import { SendOption } from "@models/annual-statements/annual-statement-enums";
import { ReminderSendingType } from "@models/reminders/reminder.enum";
import { IndividualData } from "@models/individuals/individual-data";
import { ReminderRecord } from '@models/reminders/reminder-record';
import { AdHocSigningReminderModel } from "@models/reminders/ad-hoc-signing-reminder-model";
import { TextMessageTemplateKey } from "@models/text-messages-templates/text-message-template-key";
import { TextMessagesHelper } from "@models/text-messages-templates/text-messages-helper";
import { TextMessageTemplate } from "@models/text-messages-templates/text-message-template";
import { RadioComponent } from "@shared/controls/radio/radio.component";
import { ContactsSelectComponent } from "../../../../../forms/controls/contacts-select/contacts-select.component";
import { CommonModalFormComponent } from "@shared/components/commons/common-modal-form/common-modal-form.component";
import { BaseAdHocReminderComponent } from "../base-ad-hoc-reminder/base-ad-hoc-reminder.component";

@Component({
  selector: 'app-ad-hoc-signing-reminder',
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    NgxSkeletonLoaderModule,
    CommonModalFormComponent,
    DatePipe,
    RadioComponent,
    ShadowDomViewerComponent,
    ContactsSelectComponent,
  ],
  providers: [EmailsApiService, TextMessageApiService],
  templateUrl: 'ad-hoc-signing-reminder.component.html',
  styleUrl: 'ad-hoc-signing-reminder.component.scss'
})
export class AdHocSigningReminderComponent extends BaseAdHocReminderComponent implements OnInit {
  @Input() lastSendReminderDate: Date | null | undefined;
  @Input() lodgement: LodgementDeadline | null = null;

  @Output() sendSigningAdHocReminder = new EventEmitter<ReminderRecord>();

  changeAuthorisation: ChangeAuthorisation | undefined;
  documentSigning: DocumentSigning | null | undefined;

  override ngOnInit(): void {
    super.ngOnInit();

    this.changeAuthorisation = this.documentBase?.changeAuthorisation;
    this.documentSigning = this.documentBase?.documentSigning;

    setControlDisabled(this.form.controls.recipientPhones, true);
  }

  override selectSmsTemplate(templateCode: TextMessageTemplateKey = this.textMessageTemplateCode): void {
    if (this.smsTemplates.length) {
      const smsTemplate = this.smsTemplates
        .filter((template) => template.code === templateCode);
      this.smsTemplate = smsTemplate[0];

      const type = TextMessagesHelper.buildTextMessageTemplatePayload(templateCode).$type;
      this.smsPayloadModel = this.buildPayload(type);

      if (this.selectedRecipientPhonesContact?.fullName) {
        this.smsPayloadModel.directorFirstName = this.selectedRecipientPhonesContact?.firstName ?? '';
        this.smsPayloadModel.directorFullName = this.selectedRecipientPhonesContact?.fullName ?? '';
      }

      const replacedSmsTemplate = this.smsTemplate.template
        .replace('{{DocumentType}}', this.smsPayloadModel.documentType ?? '')
        .replace('{{CompanyName}}', this.smsPayloadModel.companyName ?? '')
        .replace('{{AnnualReviewFeeDeadline}}', this.smsPayloadModel.annualReviewFeeDeadline ?? '')
        .replace('{{OrganisationName}}', this.smsPayloadModel?.organisationName ?? '')
        .replace('{{DirectorFirstName}}', this.smsPayloadModel?.directorFirstName ?? '')
        .replace('{{LodgementDeadline}}', this.smsPayloadModel?.lodgementDeadline ?? '');

      this.smsHtmlContent.set(this.sanitizer.bypassSecurityTrustHtml(replacedSmsTemplate));
    }
  }

  buildPayload(type: string): Partial<IEmailAndTextTemplatePayloadData> {
    const company = this.documentBase?.company ?? this.company;
    const billingContactFullName = (company?.billingContact?.firstName ?? '') + ' '
      + (company?.billingContact?.lastName ?? '');

    const payload = {
      $type: type,
      organisationName: this.organisationProfile.organisationName ?? '',
      organisationEmail: this.organisationProfile.email ?? '',
      organisationPhone: this.organisationProfile.phoneNumber ?? '',
      companyName: company?.name ?? '',
      username: this.organisationService.getCurrentUser()?.fullName ?? '',
      directorFirstName: this.changeAuthorisation?.authorisingParty?.individualDataOverride?.firstName ?? this.changeAuthorisation?.chairperson?.individualDataOverride?.firstName ?? '',
      directorFullName: this.changeAuthorisation?.authorisingParty?.individualDataOverride?.fullNameLabel ?? this.changeAuthorisation?.chairperson?.individualDataOverride?.fullNameLabel ?? '',
      companyACN: company?.acn ?? '',
      partner: company?.partnerManager?.fullName ?? '',
      asicContact: '',
      accountManager: company?.accountManager?.fullName ?? '',
      billingContactFirstName: company?.billingContact?.firstName ?? '',
      billingContactFullName: billingContactFullName.trim() ?? '',
      documentType: DocumentTypePipe.formatValue(this.documentBase?.type),
    };

    if (this.isAnnualReminder) {
      return {
        ...payload,
        annualReviewFeeDeadline: this.annualPaymentDeadline ? formatDate(this.annualPaymentDeadline, 'dd MMM yyyy', 'en-US') : '',
        annualReviewFeeDeadlineShortFormat: this.annualPaymentDeadline ? formatDate(this.annualPaymentDeadline, 'dd/MM/yyyy', 'en-US') : '',
      };
    } else {
      return {
        ...payload,
        documentName: `ASIC ${ DocumentTypePipe.formatValue(this.documentBase?.type ?? '') }`,
        lodgementDeadline: this.lodgement?.lodgementDeadline ? formatDate(this.lodgement?.lodgementDeadline, 'dd MMM yyyy', 'en-US') : '',
        lodgementDeadlineShortFormat: this.lodgement?.lodgementDeadline ? formatDate(this.lodgement?.lodgementDeadline, 'dd/MM/yyyy', 'en-US') : '',
        tenDaysBeforeLodgementDeadline: '',
        tenDaysBeforeLodgementDeadlineShortFormat: ''
      };
    }
  }

  override selectEmailTemplate(templateCode: EmailTemplateKey = this.emailTemplateCode): void {
    const emailTemplate = this.emailTemplates.filter(template => template.code === templateCode);

    if (emailTemplate.length) {
      this.emailTemplate = emailTemplate[0];
      const type = EmailTemplatesHelper.buildEmailTemplatePayload(templateCode).$type;
      this.emailPayloadModel = this.buildPayload(type);

      if (this.selectedRecipientEmailContact?.fullName) {
        this.emailPayloadModel.directorFirstName = this.selectedRecipientEmailContact?.firstName ?? '';
        this.emailPayloadModel.directorFullName = this.selectedRecipientEmailContact?.fullName ?? '';
      }

      this.loadPreview()
        .subscribe({
          next: () => this.isLoading = false,
          error: (error) => {
            console.warn('[loadPreview] error: ', error);
          }
        });
    }
  }

  loadPreview(): Observable<IEmailPreviewResponse> {
    const errorFormResponse = of({ subject: '', htmlContent: '' });

    return this.emailTemplatesService.loadPreview(EmailTemplatesHelper.buildEmailPreviewRequest(this.buildTemplate(), this.emailPayloadModel as EmailPayloadBase))
      .pipe(
        tap({
          next: (res) => {
            this.emailHtmlContent.set(this.sanitizer.bypassSecurityTrustHtml(res.htmlContent) ?? '');
            this.emailTemplate.subjectTemplate = EmailTemplatesHelper.prepareTemplateString(res.subject);
          },
          error: (error) => {
            console.warn('[loadPreview] error: ', error);
            this.toastr.error("Something happened while loading preview", "Error");
            this.isLoading = false;
          }
        }),
        catchError(() => errorFormResponse),
      );
  }

  buildTemplate(): EmailTemplate {
    return new EmailTemplate({
      ...this.emailTemplate,
      subjectTemplate: this.emailTemplate?.subjectTemplate,
      bodyTemplate: this.emailTemplate.bodyTemplate,
      footerTemplate: this.emailTemplate.footerTemplate,
    });
  }

  buildMessageTemplate(): TextMessageTemplate {
    return new TextMessageTemplate({
      textMessageTemplateId: Guid.EmptyGuid,
      code: this.smsTemplate.code,
      template: this.smsTemplate.template
    });
  }

  sendReminder(): void {
    if (this.form.invalid || this.isLoading || !this.isSelectedContactValid()) {
      this.form.markAllAsTouched();
      this.toastr.error('Please fill required fields', 'Error');
      return;
    }

    this.isLoading = true;
    this.form.disable();

    const sendingType = this.form.controls.sendBy.value === SendOption.Email ? ReminderSendingType.Email : ReminderSendingType.SMS;
    const emailRecord = {};
    let phones: string[] = [];

    if (sendingType === ReminderSendingType.Email && this.form.controls.recipientEmails.value) {
      emailRecord[this.form.controls.recipientEmails.value] = this.selectedRecipientEmailContact?.email;
    } else if (sendingType === ReminderSendingType.SMS && this.form.controls.recipientPhones.value) {
      phones = [this.selectedRecipientPhonesContact?.phone!];
    }

    const body = {
      documentId: this.documentSigning?.documentId,
      entityId: this.documentBase?.entityId,
      sendingType,
      emails: emailRecord,
      phones,
      emailPayloadModel: this.emailPayloadModel,
      emailTemplate: this.buildTemplate(),
      textPayloadModel: this.smsPayloadModel,
      textTemplate: this.buildMessageTemplate()
    } as AdHocSigningReminderModel;

    this.remindersService.sendSigningAdHocReminder(body)
      .subscribe({
        next: (reminder) => {
          this.toastr.success("The signing reminder has been sent", "Success");
          this.sendSigningAdHocReminder.emit(reminder);
          this.activeModal.close();
        },
        error: (error) => {
          this.toastr.error("The signing reminder hasn't been sent", "Error");
          console.warn('[sendSigningAdHocReminder] error: ', error);
          this.onSubmitRequestError();
        }
      });
  }

  // ===== handle contacts =====

  override getContacts(): Observable<Contact[]> {
    const contacts = this.documentBase?.documentSigning?.signatures.map((signature) => {
      const signatory = signature.signatory;

      return new Contact({
        fullName: IndividualData.buildFullNameWithMiddleLabel(signatory),
        firstName: signatory.firstName,
        lastName: signatory.lastName,
        contactId: signature.signatoryId,
        email: signature.email,
        phone: signature.phone,
        nickname: 'Signatory'
      });
    }) ?? [];

    return of(contacts);
  }

  override onModalDataLoaded(): void {
    const recipientEmails = this.contacts.find((contact) => contact.email.trim()) ?? this.contacts[0];
    const recipientPhones = this.contacts.find((contact) => contact.phone.trim()) ?? this.contacts[0];

    this.form.patchValue({
      recipientEmails: recipientEmails?.contactId,
      recipientPhones: recipientPhones?.contactId,
    });

    this.onRecipientEmailChanged(recipientEmails ?? null);
    this.onRecipientPhoneChanged(recipientPhones ?? null);
  }

  override onRecipientEmailChanged(contact: Contact | null) {
    super.onRecipientEmailChanged(contact);
    this.selectEmailTemplate();
  }

  override onRecipientPhoneChanged(contact: Contact | null): void {
    super.onRecipientPhoneChanged(contact);
    this.selectSmsTemplate();
  }

  override get textMessageTemplateCode(): TextMessageTemplateKey {
    return this.isAnnualReminder
      ? 'as-signing-reminder'
      : 'asic-document-siging-reminder';
  }

  override get emailTemplateCode(): EmailTemplateKey {
    return this.isAnnualReminder
      ? 'as-esign-reminder'
      : 'asic-documents-esign-reminder';
  }
}
